const BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';

export function encodeGeohash(lat: number, lng: number, precision = 6): string {
  let idx = 0, bit = 0, evenBit = true, geohash = '';
  let minLat = -90, maxLat = 90, minLng = -180, maxLng = 180;
  while (geohash.length < precision) {
    if (evenBit) {
      const mid = (minLng + maxLng) / 2;
      if (lng >= mid) { idx = idx * 2 + 1; minLng = mid; } else { idx = idx * 2; maxLng = mid; }
    } else {
      const mid = (minLat + maxLat) / 2;
      if (lat >= mid) { idx = idx * 2 + 1; minLat = mid; } else { idx = idx * 2; maxLat = mid; }
    }
    evenBit = !evenBit;
    if (++bit === 5) { geohash += BASE32[idx]; bit = 0; idx = 0; }
  }
  return geohash;
}

/**
 * Returns [lower, upper] geohash bounds that cover a bounding box.
 * Uses the common prefix of the four corners, which gives a conservative
 * prefix that contains all points in the box.
 *
 * For Firestore: where('geohash', '>=', lower).where('geohash', '<=', upper)
 */
export function geohashBoundsForBbox(
  minLat: number, minLng: number,
  maxLat: number, maxLng: number,
  precision = 6,
): [string, string] {
  const sw = encodeGeohash(minLat, minLng, precision);
  const ne = encodeGeohash(maxLat, maxLng, precision);
  // Use min/max of the two corner hashes as the range
  const lower = sw < ne ? sw : ne;
  const upper = sw < ne ? ne : sw;
  return [lower, upper];
}

/** Haversine great-circle distance in km between two lat/lng pairs. */
export function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Approximate degrees per km (good enough for a search radius).
 * 1° lat ≈ 111 km, 1° lng varies by latitude.
 */
export function bboxFromCenterRadius(
  lat: number, lng: number, radiusKm: number,
): { minLat: number; maxLat: number; minLng: number; maxLng: number } {
  const latDelta = radiusKm / 111;
  const lngDelta = radiusKm / (111 * Math.cos((lat * Math.PI) / 180));
  return {
    minLat: lat - latDelta,
    maxLat: lat + latDelta,
    minLng: lng - lngDelta,
    maxLng: lng + lngDelta,
  };
}
